import React, { useState, useMemo, useEffect, useRef } from 'react'

import {
	ArrowLeft,
	ChevronDown,
	Zap,
	Loader2,
	Search,
	Play,
	Pause,
	X,
	Volume2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Voice } from '@/core/api/entities/campaign/voice'
import { audioMixingService } from '@/core/api/services/audioMixingService'
import { elevenLabsService } from '@/core/api/services/elevenLabsService'
import { tokenService } from '@/core/api/services/tokenService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useUploadAdvert } from '@/core/hooks/useAdvert'
import { useAuth } from '@/core/hooks/useAuth'
import {
	usePaginateCampaigns,
	useUpdateCampaign,
	useVoices,
} from '@/core/hooks/useCampaign'
import { useTranslation } from '@/core/i18n/useTranslation'
import { convertWavBlobToMp3 } from '@/core/utils/audio/mp3Encoder'
import { useVoicesCatalog } from '@/features/campaigns/hooks/useVoicesCatalog'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Separator } from '@/shared/components/atoms/Separator'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	Tabs,
	TabsList,
	TabsTrigger,
	TabsContent,
} from '@/shared/components/atoms/Tabs'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { GuidedTour } from '@/shared/components/organisms/GuidedTour'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { AudioPlayer } from './components/AudioPlayer'
import { EditAudioModal } from './components/EditAudioModal'
import { NoTokensModal } from './components/NoTokensModal'
import { RecordAudio } from './components/RecordAudio'
import { MUSIC_GENRE_GROUPS, getMusicGenreUrl } from './constants/musicGenres'

// Music padding constants (in seconds)
const MUSIC_PADDING_BEFORE = 1.5 // Music starts 1.5 seconds before voice
const MUSIC_PADDING_AFTER = 3.0 // Music ends 3 seconds after voice

const CreateAICampaignPage: React.FC = () => {
	const { t } = useTranslation('campaigns')
	const navigate = useNavigate()
	const { showNotification } = useNotificationContext()
	const [scriptText, setScriptText] = useState('')
	const [selectedCollectionId, setSelectedCollectionId] = useState<string>('')
	const [selectedVoice, setSelectedVoice] = useState('')
	const [selectedMusicGroup, setSelectedMusicGroup] = useState('')
	const [selectedSong, setSelectedSong] = useState('')
	const [showSpeakerSettings, setShowSpeakerSettings] = useState(false)
	const [showMobileEditSettings, setShowMobileEditSettings] = useState(false)

	const TOUR_KEY = 'ai_ads_guide_shown_v1'
	const [tourOpen, setTourOpen] = useState(false)

	useEffect(() => {
		try {
			const shown = localStorage.getItem(TOUR_KEY)
			if (!shown) setTourOpen(true)
		} catch {
			setTourOpen(true)
		}
	}, [])

	const [speed, setSpeed] = useState([50])
	const [expressiveness, setExpressiveness] = useState([50])
	const [clarity, setClarity] = useState([75])
	const [styleExaggeration, setStyleExaggeration] = useState([75])

	const [isGenerating, setIsGenerating] = useState(false)
	const [isMixingAudio, setIsMixingAudio] = useState(false)
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [assignSearchQuery, setAssignSearchQuery] = useState('')
	const [selectedAssignCampaignId, setSelectedAssignCampaignId] = useState<
		number | null
	>(null)
	const [isAssigningCampaign, setIsAssigningCampaign] = useState(false)
	const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string | null>(
		null
	)
	const [generatedAudioBlob, setGeneratedAudioBlob] = useState<Blob | null>(
		null
	)
	const [originalVoiceBlob, setOriginalVoiceBlob] = useState<Blob | null>(null)

	const [lastGenerationParams, setLastGenerationParams] = useState<{
		text: string
		voiceId: string
		speed: number
		expressiveness: number
		clarity: number
		styleExaggeration: number
	} | null>(null)

	const [isEditModalOpen, setIsEditModalOpen] = useState(false)

	const [activeTab, setActiveTab] = useState('text')
	const [shouldProcessRecording, setShouldProcessRecording] = useState(false)
	const [voiceSelectOpen, setVoiceSelectOpen] = useState(false)
	const [musicSelectOpen, setMusicSelectOpen] = useState(false)
	const [songSelectOpen, setSongSelectOpen] = useState(false)
	const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
	const [isAudioPaused, setIsAudioPaused] = useState(false)
	const [playingMusicId, setPlayingMusicId] = useState<string | null>(null)
	const [isMusicPaused, setIsMusicPaused] = useState(false)
	const [mobileVoiceVolume, setMobileVoiceVolume] = useState([80])
	const currentPlayingMusicRef = useRef<HTMLAudioElement | null>(null)

	// Persist last chosen music segment for the EditAudioModal
	const [musicRegionForEditor, setMusicRegionForEditor] = useState<{
		start: number
		end: number
	} | null>(null)

	const maxCharacters = 400

	const [showNoTokensModal, setShowNoTokensModal] = useState(false)

	const { user: currentUser } = useAuth()

	const rawAccount = currentUser?.account

	const accountForTokens = rawAccount
		? {
				account_type: rawAccount.accountType,
				features: rawAccount.features,
			}
		: null

	const tokensTotal = tokenService.getMaxTokens(accountForTokens)
	const tokensAvailable = tokenService.getAvailableTokens(accountForTokens)

	const {
		data: voices,
		isLoading: isLoadingVoices,
		isError: isVoicesError,
	} = useVoices({})
	const { data: campaignsResponse, isLoading: isLoadingAssignCampaigns } =
		usePaginateCampaigns({
			config: useAccountConfig({
				params: {
					per_page: 100,
					include: 'adverts',
				},
			}),
			options: {
				enabled: isAssignModalOpen,
			},
		})
	const uploadAdvertMutation = useUploadAdvert({
		config: useAccountConfig({}),
	})
	const updateCampaignMutation = useUpdateCampaign({
		config: useAccountConfig({}),
	})

	const handleCancel = () => {
		navigate('/campaigns')
	}

	const handleAssignToCampaign = () => {
		setIsAssignModalOpen(true)
	}

	const clearGeneratedAudio = () => {
		if (generatedAudioUrl) {
			URL.revokeObjectURL(generatedAudioUrl)
		}
		setGeneratedAudioUrl(null)
		setGeneratedAudioBlob(null)
		setOriginalVoiceBlob(null)
		setRawRecordingBlob(null)
		setLastGenerationParams(null)
		setSelectedMusicGroup('')
		setSelectedSong('')
		setShowMobileEditSettings(false)
	}

	const handleCloseAssignModal = () => {
		setIsAssignModalOpen(false)
		setAssignSearchQuery('')
		setSelectedAssignCampaignId(null)
	}

	const handleTabChange = (newTab: string) => {
		if (generatedAudioUrl) {
			URL.revokeObjectURL(generatedAudioUrl)
		}
		setGeneratedAudioUrl(null)
		setGeneratedAudioBlob(null)
		setOriginalVoiceBlob(null)
		setRawRecordingBlob(null)
		setScriptText('')
		setLastGenerationParams(null)
		setShowMobileEditSettings(false)
		mixedAudioRefRef.current = null
		isFromEditorRef.current = false
		setActiveTab(newTab)
	}

	const [rawRecordingBlob, setRawRecordingBlob] = useState<Blob | null>(null)

	const handleRecordingComplete = (recordedBlob: Blob) => {
		const audioUrl = elevenLabsService.createAudioUrl(recordedBlob)
		setRawRecordingBlob(recordedBlob)
		setGeneratedAudioBlob(recordedBlob)
		setGeneratedAudioUrl(audioUrl)

		showNotification({
			type: 'success',
			message: t('ai.notifications.success'),
			description: t('ai.notifications.recordingSuccess'),
		})
	}

	const handleProcessSpeech = async (recordedBlob: Blob) => {
		if (tokensAvailable <= 0) {
			setShowNoTokensModal(true)
			return
		}

		setIsGenerating(true)

		try {
			if (!selectedVoice) {
				showNotification({
					type: 'error',
					message: t('ai.notifications.error'),
					description: t('ai.notifications.selectVoiceError'),
				})
				setIsGenerating(false)
				return
			}

			if (generatedAudioUrl) {
				URL.revokeObjectURL(generatedAudioUrl)
			}

			const audioBlob = await elevenLabsService.speechToSpeech(recordedBlob, {
				voiceId: selectedVoice,
				stability: clarity[0],
				similarityBoost: clarity[0],
				style: styleExaggeration[0],
				useSpeakerBoost: clarity[0] > 50,
				modelId: 'eleven_multilingual_sts_v2',
				removeBackgroundNoise: true,
			})

			const audioUrl = elevenLabsService.createAudioUrl(audioBlob)

			setOriginalVoiceBlob(audioBlob)
			setGeneratedAudioBlob(audioBlob)
			setGeneratedAudioUrl(audioUrl)

			mixedAudioRefRef.current = null
			isFromEditorRef.current = false

			setScriptText(t('ai.notifications.processedScript'))

			setLastGenerationParams({
				text: t('ai.notifications.processedScript'),
				voiceId: selectedVoice,
				speed: speed[0],
				expressiveness: expressiveness[0],
				clarity: clarity[0],
				styleExaggeration: styleExaggeration[0],
			})

			tokenService.useToken(accountForTokens)

			setRawRecordingBlob(null)

			showNotification({
				type: 'success',
				message: t('ai.notifications.success'),
				description: t('ai.notifications.convertSuccess'),
			})
		} catch (error) {
			console.error('Error converting speech:', error)
			showNotification({
				type: 'error',
				message: t('ai.notifications.error'),
				description:
					error instanceof Error
						? error.message
						: t('ai.notifications.convertError'),
			})
		} finally {
			setIsGenerating(false)
		}
	}

	const handleGenerateSpeech = async () => {
		if (activeTab === 'recording') {
			setShouldProcessRecording(true)
			return
		}

		if (!scriptText.trim()) {
			showNotification({
				type: 'error',
				message: t('ai.notifications.error'),
				description: t('ai.notifications.textError'),
			})
			return
		}

		if (!selectedVoice) {
			showNotification({
				type: 'error',
				message: t('ai.notifications.error'),
				description: t('ai.notifications.selectVoiceError'),
			})
			return
		}

		if (tokensAvailable <= 0) {
			setShowNoTokensModal(true)
			return
		}

		setIsGenerating(true)

		try {
			if (generatedAudioUrl) {
				URL.revokeObjectURL(generatedAudioUrl)
			}

			const audioBlob = await elevenLabsService.generateSpeech({
				text: scriptText,
				voiceId: selectedVoice,
				speed: speed[0],
				expressiveness: expressiveness[0],
				clarity: clarity[0],
				styleExaggeration: styleExaggeration[0],
			})

			const audioUrl = elevenLabsService.createAudioUrl(audioBlob)

			setOriginalVoiceBlob(audioBlob)
			setGeneratedAudioBlob(audioBlob)
			setGeneratedAudioUrl(audioUrl)

			setLastGenerationParams({
				text: scriptText,
				voiceId: selectedVoice,
				speed: speed[0],
				expressiveness: expressiveness[0],
				clarity: clarity[0],
				styleExaggeration: styleExaggeration[0],
			})

			tokenService.useToken(accountForTokens)

			showNotification({
				type: 'success',
				message: t('ai.notifications.success'),
				description: t('ai.notifications.generateSuccess'),
			})
		} catch (error) {
			console.error('Error generating speech:', error)
			showNotification({
				type: 'error',
				message: t('ai.notifications.error'),
				description:
					error instanceof Error
						? error.message
						: t('ai.notifications.generateError'),
			})
		} finally {
			setIsGenerating(false)
		}
	}

	const handleDownloadAudio = async () => {
		if (!generatedAudioBlob) return

		// Default: MP3 filename
		let blobToDownload = generatedAudioBlob
		let filename = 'campaign_audio.mp3'

		try {
			// If we have a WAV (typically mixed audio), convert to MP3 for download only
			if (generatedAudioBlob.type === 'audio/wav') {
				blobToDownload = await convertWavBlobToMp3(generatedAudioBlob)
			} else if (
				!generatedAudioBlob.type ||
				generatedAudioBlob.type === 'audio/mpeg'
			) {
				// already MP3-compatible, keep as is
				blobToDownload = generatedAudioBlob
			} else {
				// Unknown type – fall back to original blob with generic extension
				filename = 'campaign_audio'
			}
		} catch (error) {
			console.error(
				'Error converting audio to MP3, falling back to original',
				error
			)
			blobToDownload = generatedAudioBlob
			filename =
				generatedAudioBlob.type === 'audio/wav'
					? 'campaign_audio.wav'
					: 'campaign_audio'
		}

		elevenLabsService.downloadAudio(blobToDownload, filename)
	}

	const handleSaveEditedAudio = async (
		musicGroup: string,
		song: string,
		musicStart: number,
		musicEnd: number,
		voiceVolume: number,
		musicVolume: number
	) => {
		setSelectedMusicGroup(musicGroup)
		setSelectedSong(song)

		// Persist the chosen segment so the modal can restore it next time
		setMusicRegionForEditor(
			musicStart < musicEnd ? { start: musicStart, end: musicEnd } : null
		)

		if (
			musicGroup &&
			musicGroup !== 'none' &&
			originalVoiceBlob &&
			musicVolume > 0
		) {
			try {
				setIsGenerating(true)
				setIsMixingAudio(true)

				const musicUrl = getMusicGenreUrl(selectedSong)
				if (!musicUrl) {
					throw new Error('Music URL not found')
				}

				// Selector in editor represents total music duration (voice + paddings)
				const outputDuration = Math.max(0, musicEnd - musicStart)
				const adjustedMusicStart = Math.max(0, musicStart)
				const adjustedMusicEnd = musicEnd

				const mixedAudioBlob = await audioMixingService.mixAudio(
					originalVoiceBlob,
					musicUrl,
					adjustedMusicStart,
					adjustedMusicEnd,
					voiceVolume,
					musicVolume,
					outputDuration,
					MUSIC_PADDING_BEFORE
				)

				if (generatedAudioUrl) {
					URL.revokeObjectURL(generatedAudioUrl)
				}

				const mixedAudioUrl = elevenLabsService.createAudioUrl(mixedAudioBlob)
				setGeneratedAudioBlob(mixedAudioBlob)
				setGeneratedAudioUrl(mixedAudioUrl)

				isFromEditorRef.current = true
				mixedAudioRefRef.current = null

				showNotification({
					type: 'success',
					message: t('ai.notifications.audioMixed'),
					description: t('ai.notifications.mixSuccess'),
				})
			} catch (error) {
				console.error('Error mixing audio:', error)
				showNotification({
					type: 'error',
					message: t('ai.notifications.error'),
					description:
						error instanceof Error
							? error.message
							: t('ai.notifications.mixError'),
				})
			} finally {
				setIsGenerating(false)
				setIsMixingAudio(false)
			}
		} else if (musicVolume === 0 && originalVoiceBlob) {
			const audioUrl = elevenLabsService.createAudioUrl(originalVoiceBlob)

			if (generatedAudioUrl) {
				URL.revokeObjectURL(generatedAudioUrl)
			}

			setGeneratedAudioBlob(originalVoiceBlob)
			setGeneratedAudioUrl(audioUrl)

			isFromEditorRef.current = true
			mixedAudioRefRef.current = null

			showNotification({
				type: 'success',
				message: t('ai.notifications.changesSaved'),
				description: t('ai.notifications.musicRemoved'),
			})
		} else if (originalVoiceBlob) {
			const audioUrl = elevenLabsService.createAudioUrl(originalVoiceBlob)

			if (generatedAudioUrl) {
				URL.revokeObjectURL(generatedAudioUrl)
			}

			setGeneratedAudioBlob(originalVoiceBlob)
			setGeneratedAudioUrl(audioUrl)

			isFromEditorRef.current = true
			mixedAudioRefRef.current = null

			showNotification({
				type: 'success',
				message: t('ai.notifications.changesSaved'),
				description: t('ai.notifications.editsSaved'),
			})
		}
	}

	const mixedAudioRefRef = useRef<string | null>(null)
	const isFromEditorRef = useRef<boolean>(false)
	const isPlayButtonClickRef = useRef<boolean>(false)
	const currentPlayingAudioRef = useRef<HTMLAudioElement | null>(null)

	// When user selects "Don't use music", revert to the original voice-only audio
	useEffect(() => {
		if (
			selectedMusicGroup === 'none' &&
			originalVoiceBlob &&
			generatedAudioBlob &&
			generatedAudioBlob !== originalVoiceBlob
		) {
			try {
				if (generatedAudioUrl) {
					URL.revokeObjectURL(generatedAudioUrl)
				}

				const dryUrl = elevenLabsService.createAudioUrl(originalVoiceBlob)
				setGeneratedAudioBlob(originalVoiceBlob)
				setGeneratedAudioUrl(dryUrl)

				// Reset mixing flags
				isFromEditorRef.current = false
				mixedAudioRefRef.current = null
			} catch (error) {
				console.error('Error reverting to voice-only audio:', error)
			}
		}
	}, [selectedMusicGroup, originalVoiceBlob])

	useEffect(() => {
		const mixAudioWithMusic = async () => {
			if (
				!selectedMusicGroup ||
				selectedMusicGroup === 'none' ||
				!originalVoiceBlob ||
				!selectedSong
			) {
				mixedAudioRefRef.current = null
				isFromEditorRef.current = false
				return
			}

			const mixKey = `${originalVoiceBlob.size}-${selectedSong}`

			if (isFromEditorRef.current && mixedAudioRefRef.current === mixKey) {
				return
			}
			if (isFromEditorRef.current && mixedAudioRefRef.current !== mixKey) {
				isFromEditorRef.current = false
			}

			if (mixedAudioRefRef.current === mixKey) {
				return
			}

			try {
				setIsMixingAudio(true)
				const musicUrl = getMusicGenreUrl(selectedSong)
				if (!musicUrl) return

				if (!originalVoiceBlob) return

				const audioContext = new (window.AudioContext ||
					(window as any).webkitAudioContext)()
				const voiceArrayBuffer = await originalVoiceBlob.arrayBuffer()
				const voiceAudioBuffer =
					await audioContext.decodeAudioData(voiceArrayBuffer)
				const voiceDuration = voiceAudioBuffer.duration

				const musicResponse = await fetch(musicUrl)
				const musicBuffer = await musicResponse.arrayBuffer()
				const musicAudioBuffer = await audioContext.decodeAudioData(musicBuffer)
				const musicDuration = musicAudioBuffer.duration

				// Calculate padded output duration
				const outputDuration =
					voiceDuration + MUSIC_PADDING_BEFORE + MUSIC_PADDING_AFTER

				// Calculate music start/end with padding
				// Music should start before voice and end after voice
				const musicStartTime = Math.max(0, (musicDuration - outputDuration) / 2)
				const musicEndTime = Math.min(
					musicDuration,
					musicStartTime + outputDuration
				)

				const voiceVolume = 1.0
				const musicVolume = 0.4

				const mixedAudioBlob = await audioMixingService.mixAudio(
					originalVoiceBlob,
					musicUrl,
					musicStartTime,
					musicEndTime,
					voiceVolume,
					musicVolume,
					outputDuration,
					MUSIC_PADDING_BEFORE
				)

				if (generatedAudioUrl) {
					URL.revokeObjectURL(generatedAudioUrl)
				}

				const mixedAudioUrl = elevenLabsService.createAudioUrl(mixedAudioBlob)
				setGeneratedAudioBlob(mixedAudioBlob)
				setGeneratedAudioUrl(mixedAudioUrl)
				mixedAudioRefRef.current = mixKey
			} catch (error) {
				console.error('Error auto-mixing audio:', error)
			} finally {
				setIsMixingAudio(false)
			}
		}

		mixAudioWithMusic()
	}, [selectedMusicGroup, selectedSong, originalVoiceBlob])

	useEffect(() => {
		return () => {
			if (generatedAudioUrl) {
				URL.revokeObjectURL(generatedAudioUrl)
			}
		}
	}, [generatedAudioUrl])

	const { voicesByCollection, collectionOptions } = useVoicesCatalog(voices)

	const campaigns = campaignsResponse?.data || []
	const filteredAssignCampaigns = useMemo(() => {
		const query = assignSearchQuery.trim().toLowerCase()
		if (!query) return campaigns
		return campaigns.filter((campaign) =>
			campaign.name.toLowerCase().includes(query)
		)
	}, [campaigns, assignSearchQuery])

	const getAdsCountLabel = (count: number) => {
		return count === 1
			? t('ai.assign_modal.ads_count_one', { count })
			: t('ai.assign_modal.ads_count_other', { count })
	}

	const handleConfirmAssignToCampaign = async () => {
		if (
			!generatedAudioBlob ||
			!selectedAssignCampaignId ||
			isAssigningCampaign ||
			uploadAdvertMutation.isPending ||
			updateCampaignMutation.isPending
		) {
			return
		}

		const campaign = campaigns.find((c) => c.id === selectedAssignCampaignId)
		if (!campaign) return

		setIsAssigningCampaign(true)
		try {
			let blobToUpload = generatedAudioBlob
			if (generatedAudioBlob.type === 'audio/wav') {
				blobToUpload = await convertWavBlobToMp3(generatedAudioBlob)
			}

			const extension = blobToUpload.type === 'audio/mpeg' ? 'mp3' : 'audio'
			const fileBaseName = (scriptText.trim() || 'ai_ad')
				.slice(0, 40)
				.replace(/[^\w\s-]/g, '')
				.trim()
				.replace(/\s+/g, '_')
				.toLowerCase()

			const uploadFile = new File(
				[blobToUpload],
				`${fileBaseName || 'ai_ad'}.${extension}`,
				{
					type: blobToUpload.type || 'audio/mpeg',
				}
			)

			const formData = new FormData()
			formData.append('media', uploadFile)
			formData.append('name', (fileBaseName || 'ai ad').replace(/_/g, ' '))

			const uploadResponse = await uploadAdvertMutation.mutateAsync(formData)
			const uploadedAdvertId = uploadResponse.data.data.id

			const existingAdvertIds = (campaign.adverts || []).map((ad) => ad.id)
			const mergedAdvertIds = Array.from(
				new Set([...existingAdvertIds, uploadedAdvertId])
			)

			await updateCampaignMutation.mutateAsync({
				id: campaign.id,
				data: {
					name: campaign.name,
					rrule: campaign.rrule,
					rrule_type: campaign.rruleType,
					type: campaign.type,
					priority: campaign.priority,
					exdate: campaign.exdate || [],
					adverts: mergedAdvertIds.map((id) => ({ id })),
					account_id: campaign.accountId,
				},
			})

			showNotification({
				type: 'success',
				message: t('ai.notifications.success'),
				description: t('ai.assign_modal.assign_success'),
			})
			handleCloseAssignModal()
		} catch (error) {
			console.error('Error assigning AI ad to campaign:', error)
			showNotification({
				type: 'error',
				message: t('ai.notifications.error'),
				description:
					error instanceof Error
						? error.message
						: t('ai.assign_modal.assign_error'),
			})
		} finally {
			setIsAssigningCampaign(false)
		}
	}

	const filteredVoices = useMemo(() => {
		if (!voices) return []
		if (!selectedCollectionId) return []
		const list = voicesByCollection[selectedCollectionId] || []
		// Keep voice list stable + user-friendly: alphabetic by display name
		return [...list].sort((a, b) =>
			a
				.getDisplayName()
				.localeCompare(b.getDisplayName(), undefined, { sensitivity: 'base' })
		)
	}, [voices, voicesByCollection, selectedCollectionId])

	useEffect(() => {
		if (selectedCollectionId) return
		if (collectionOptions.length === 0) return
		setSelectedCollectionId(collectionOptions[0].value)
	}, [selectedCollectionId, collectionOptions])
	const availableMusicGenres = useMemo(() => {
		if (!selectedMusicGroup) return []
		const group = MUSIC_GENRE_GROUPS.find((g) => g.label === selectedMusicGroup)
		return group?.genres || []
	}, [selectedMusicGroup])

	useEffect(() => {
		if (selectedMusicGroup && availableMusicGenres.length > 0) {
			setSelectedSong('')
		}
	}, [selectedMusicGroup, availableMusicGenres])

	useEffect(() => {
		// When switching voice catalogs or filters, do NOT auto-select a voice.
		// The selector should start from the placeholder ("Select a voice").
		if (!selectedVoice) return

		const stillExists = filteredVoices.some((v) => v.voiceId === selectedVoice)
		if (!stillExists) {
			setSelectedVoice('')
		}
	}, [filteredVoices, selectedVoice])

	// Generic audio playback handler
	const handleAudioPlayback = async (
		id: string,
		currentId: string | null,
		currentAudio: React.MutableRefObject<HTMLAudioElement | null>,
		getAudio: () => HTMLAudioElement | null,
		setId: (id: string | null) => void,
		setPaused: (paused: boolean) => void
	) => {
		// If the same item is playing, toggle pause/play
		if (currentId === id && currentAudio.current) {
			if (!currentAudio.current.paused) {
				// Pause
				currentAudio.current.pause()
				setPaused(true)
			} else {
				// Resume
				try {
					await currentAudio.current.play()
					setPaused(false)
				} catch (error) {
					console.error('Error resuming audio:', error)
				}
			}
			return
		}

		// Stop any currently playing audio
		if (currentAudio.current) {
			currentAudio.current.pause()
			currentAudio.current.currentTime = 0
			currentAudio.current = null
		}

		// Play the new demo
		const audio = getAudio()
		if (audio) {
			try {
				await audio.play()
				currentAudio.current = audio
				setId(id)
				setPaused(false)

				// Clean up when audio ends
				audio.addEventListener('ended', () => {
					currentAudio.current = null
					setId(null)
					setPaused(false)
				})

				// Update pause state when audio is paused/resumed
				audio.addEventListener('pause', () => {
					setPaused(true)
				})
				audio.addEventListener('play', () => {
					setPaused(false)
				})
			} catch (error) {
				console.error('Error playing audio:', error)
			}
		} else {
			console.warn('No audio available for id', id)
		}
	}

	const handleVoiceDemoPlayback = (voice: Voice) => {
		handleAudioPlayback(
			voice.voiceId,
			playingVoiceId,
			currentPlayingAudioRef,
			() => voice.playVoiceDemoForVoice(),
			setPlayingVoiceId,
			setIsAudioPaused
		)
	}

	const handleMusicDemoPlayback = (musicId: string) => {
		const musicUrl = getMusicGenreUrl(musicId)
		if (!musicUrl) {
			console.warn('No preview URL available for music', musicId)
			return
		}

		handleAudioPlayback(
			musicId,
			playingMusicId,
			currentPlayingMusicRef,
			() => new Audio(musicUrl),
			setPlayingMusicId,
			setIsMusicPaused
		)
	}

	const hasParametersChanged = useMemo(() => {
		if (!lastGenerationParams) return true

		return (
			lastGenerationParams.text !== scriptText ||
			lastGenerationParams.voiceId !== selectedVoice ||
			lastGenerationParams.speed !== speed[0] ||
			lastGenerationParams.expressiveness !== expressiveness[0] ||
			lastGenerationParams.clarity !== clarity[0] ||
			lastGenerationParams.styleExaggeration !== styleExaggeration[0]
		)
	}, [
		lastGenerationParams,
		scriptText,
		selectedVoice,
		speed,
		expressiveness,
		clarity,
		styleExaggeration,
	])

	return (
		<AuthenticatedLayout requireAuth={true}>
			<GuidedTour
				open={tourOpen}
				onOpenChange={setTourOpen}
				storageKey={TOUR_KEY}
				steps={[
					{
						id: 'text',
						selector: '[data-tour="ai-ads-text"]',
						title: t('ai_ads_guide.step1.title'),
						description: t('ai_ads_guide.step1.description'),
					},
					{
						id: 'voice',
						selector: '[data-tour="ai-ads-voice"]',
						title: t('ai_ads_guide.step2.title'),
						description: t('ai_ads_guide.step2.description'),
					},
					{
						id: 'music',
						selector: '[data-tour="ai-ads-music"]',
						title: t('ai_ads_guide.step3.title'),
						description: t('ai_ads_guide.step3.description'),
					},
					{
						id: 'generate',
						selector: '[data-tour="ai-ads-generate"]',
						selectorMobile: '[data-tour="ai-ads-generate-mobile"]',
						title: t('ai_ads_guide.step4.title'),
						description: t('ai_ads_guide.step4.description'),
					},
				]}
			/>

			<div
				className={`fixed inset-0 z-40 bg-black/30 flex items-end md:static md:z-auto md:bg-transparent w-full h-auto min-h-100 md:px-3 2xl:pl-6 2xl:pr-6 md:py-3 2xl:py-6 md:flex md:flex-col ${
					generatedAudioUrl ? 'md:mb-32' : ''
				}`}
			>
				<div className="w-full max-w-none md:max-w-[1400px] md:mx-auto md:w-full md:flex-1 md:flex md:flex-col md:min-h-0">
					<div className="bg-card w-full h-[92dvh] rounded-t-2xl border-t border-border overflow-hidden flex flex-col min-h-0 md:h-auto md:border md:rounded-xl md:flex-1">
						<div className="md:hidden flex justify-center pt-3 pb-2">
							<div className="w-10 h-1 bg-gray-600 rounded-full" />
						</div>
						<div className="hidden md:flex bg-card lg:py-6 md:py-3 lg:px-8 md:px-3 items-center justify-between">
							<div className="flex items-center gap-4">
								<button
									onClick={handleCancel}
									className="flex items-center cursor-pointer justify-center"
								>
									<ArrowLeft className="w-6 h-6" />
								</button>
								<div className="space-y-0">
									<h2 className="flex items-center text-lg font-normal tracking-tight md:text-3xl">
										{t('ai.title')}
									</h2>
								</div>
							</div>
						</div>
						<div className="md:hidden bg-card py-3 px-4 flex items-center justify-between">
							<h2 className="text-lg font-semibold tracking-tight">
								{t('ai.title')}
							</h2>
							<div className="flex items-center gap-2">
								<div
									className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
									onClick={() => setShowNoTokensModal(true)}
								>
									<Zap className="w-3.5 h-3.5 text-muted-foreground" />
									<span className="text-xs font-medium">{`${tokensAvailable} / ${tokensTotal}`}</span>
								</div>
								<button
									type="button"
									onClick={handleCancel}
									className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
								>
									<X className="w-5 h-5" />
								</button>
							</div>
						</div>

						<Separator />

						<div className="flex flex-col lg:flex-row bg-card flex-1 overflow-y-auto">
							<div
								className={`md:flex-1 lg:px-8 py-3 lg:py-6 px-4 flex flex-col ${
									generatedAudioUrl ? 'pb-28 md:pb-0' : ''
								}`}
							>
								<div
									className={
										generatedAudioUrl && generatedAudioBlob
											? 'hidden md:block'
											: 'block'
									}
								>
									<Tabs
										value={activeTab}
										onValueChange={handleTabChange}
										className="w-full md:flex-1 flex flex-col"
									>
										<div className="md:hidden w-full rounded-full border border-border bg-background p-1 grid grid-cols-2 gap-1">
											<button
												type="button"
												onClick={() => handleTabChange('text')}
												className={`h-10 rounded-full text-base font-medium transition-colors ${
													activeTab === 'text'
														? 'bg-outline text-foreground'
														: 'text-muted-foreground'
												}`}
											>
												{t('ai.tabs.mobile_text')}
											</button>
											<button
												type="button"
												onClick={() => handleTabChange('recording')}
												className={`h-10 rounded-full text-base font-medium transition-colors ${
													activeTab === 'recording'
														? 'bg-outline text-foreground'
														: 'text-muted-foreground'
												}`}
											>
												{t('ai.tabs.mobile_recording')}
											</button>
										</div>
										<TabsList className="hidden md:inline-flex w-fit gap-1.5">
											<TabsTrigger value="text">
												{t('ai.tabs.text')}
											</TabsTrigger>
											<TabsTrigger value="recording">
												{t('ai.tabs.recording')}
											</TabsTrigger>
										</TabsList>

										<TabsContent
											value="text"
											className="space-y-4 flex-initial md:flex-1 flex flex-col"
										>
											<div className="space-y-1 md:space-y-2 flex flex-col gap-1">
												<label className="md:hidden text-sm font-medium">
													{t('ai.script_label')}
												</label>
												<div className="hidden md:flex items-center justify-between">
													<span className="text-sm text-muted-foreground">
														{maxCharacters} {t('ai.charsMax')}
													</span>
												</div>
												<Textarea
													data-tour="ai-ads-text"
													placeholder={t('ai.scriptPlaceholder')}
													value={scriptText}
													onChange={(e) => setScriptText(e.target.value)}
													maxLength={maxCharacters}
													className="min-h-[100px] bg-background border-border text-foreground resize-none"
												/>
												<div className="md:hidden text-sm text-muted-foreground">
													{maxCharacters} {t('ai.charsMax')}
												</div>
											</div>
										</TabsContent>

										<TabsContent
											value="recording"
											className="space-y-4 flex-initial md:flex-1 flex flex-col"
										>
											<div className="flex-1">
												<RecordAudio
													onRecordingComplete={handleRecordingComplete}
													onProcessSpeech={async (text) => {
														await handleProcessSpeech(text)
														setShouldProcessRecording(false)
													}}
													shouldProcessRecording={shouldProcessRecording}
												/>
											</div>
										</TabsContent>
									</Tabs>
								</div>

								<div className="hidden md:flex items-center justify-between pt-4 flex-wrap lg:flex-nowrap gap-2 lg:gap-0">
									<div className="flex items-center gap-3">
										{generatedAudioUrl ? (
											<>
												<Button
													variant="outline"
													onClick={handleGenerateSpeech}
													disabled={
														!scriptText.trim() ||
														tokensAvailable <= 0 ||
														isGenerating ||
														!hasParametersChanged
													}
													className="rounded-full px-6"
												>
													{isGenerating ? (
														<>
															<Loader2 className="w-4 h-4 mr-2 animate-spin" />
															{t('ai.buttons.regenerating')}
														</>
													) : (
														t('ai.buttons.regenerate')
													)}
												</Button>
												<Button
													onClick={
														activeTab === 'recording' &&
														rawRecordingBlob &&
														!originalVoiceBlob
															? () => setShouldProcessRecording(true)
															: handleDownloadAudio
													}
													className="rounded-full px-6"
												>
													{activeTab === 'recording' &&
													rawRecordingBlob &&
													!originalVoiceBlob
														? t('ai.buttons.process')
														: t('ai.buttons.download')}
												</Button>
											</>
										) : (
											<>
												<Button
													variant="outline"
													onClick={handleCancel}
													className="rounded-full px-6"
												>
													{t('ai.buttons.cancel')}
												</Button>
												<Button
													data-tour="ai-ads-generate"
													onClick={handleGenerateSpeech}
													disabled={
														activeTab === 'text'
															? !scriptText.trim() ||
																tokensAvailable <= 0 ||
																isGenerating
															: activeTab === 'recording' && rawRecordingBlob
																? tokensAvailable <= 0 ||
																	isGenerating ||
																	!selectedVoice
																: tokensAvailable <= 0 || isGenerating
													}
													className="rounded-full px-6"
												>
													{isGenerating ? (
														<>
															<Loader2 className="w-4 h-4 mr-2 animate-spin" />
															{activeTab === 'recording' && rawRecordingBlob
																? t('ai.buttons.processing')
																: t('ai.buttons.generating')}
														</>
													) : activeTab === 'recording' && rawRecordingBlob ? (
														t('ai.buttons.process')
													) : (
														t('ai.buttons.generate')
													)}
												</Button>
											</>
										)}
									</div>
									<div
										className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
										onClick={() => setShowNoTokensModal(true)}
									>
										<Zap className="w-4 h-4 text-muted-foreground" />
										<span className="text-sm font-medium hidden lg:block">
											{tokensAvailable > 0
												? t('ai.tokens.available', {
														count: tokensAvailable,
														total: tokensTotal,
													})
												: t('ai.tokens.none')}
										</span>
										<span className="text-sm font-medium block lg:hidden">
											{`${tokensAvailable} / ${tokensTotal}`}
										</span>
									</div>
								</div>
								<div className="md:hidden">
									{generatedAudioUrl &&
										generatedAudioBlob &&
										!isMixingAudio && (
											<div className="space-y-3 pt-4">
												<AudioPlayer
													audioUrl={generatedAudioUrl}
													audioBlob={generatedAudioBlob}
													fileName={`${scriptText.substring(0, 30)}${scriptText.length > 30 ? '...' : ''}.mp3`}
													closable={true}
													closeIcon="trash"
													onClose={clearGeneratedAudio}
												/>
												<button
													type="button"
													onClick={() =>
														setShowMobileEditSettings(!showMobileEditSettings)
													}
													className="flex items-center gap-2 text-sm font-medium hover:text-muted-foreground transition-colors"
												>
													<ChevronDown
														className={`w-4 h-4 transition-transform ${
															showMobileEditSettings ? 'rotate-180' : ''
														}`}
													/>
													{t('ai.buttons.edit_ad')}
												</button>
												{showMobileEditSettings && (
													<div className="space-y-4">
														<div className="bg-background border border-border rounded-lg p-3 space-y-3">
															<div className="flex items-center justify-between">
																<h3 className="text-base font-medium">
																	{t('ai.mobile.voice')}
																</h3>
																<div className="flex items-center gap-3">
																	<Volume2 className="h-4 w-4 text-muted-foreground" />
																	<Slider
																		value={mobileVoiceVolume}
																		onValueChange={setMobileVoiceVolume}
																		min={0}
																		max={100}
																		step={1}
																		className="w-24"
																	/>
																</div>
															</div>
															<AudioPlayer
																audioUrl={generatedAudioUrl}
																audioBlob={generatedAudioBlob}
																fileName={t('ai.mobile.voice')}
																volume={mobileVoiceVolume[0]}
																hideHeader={true}
															/>
														</div>
														<div className="space-y-2 flex flex-col gap-1">
															<label className="text-sm font-medium">
																{t('ai.settings.musicStyle')}
															</label>
															<Select
																value={selectedMusicGroup}
																onValueChange={(val) => {
																	if (isPlayButtonClickRef.current) {
																		return
																	}
																	setSelectedMusicGroup(val)
																}}
															>
																<SelectTrigger className="w-full">
																	<SelectValue
																		placeholder={t(
																			'ai.settings.selectMusicStyle'
																		)}
																	/>
																</SelectTrigger>
																<SelectContent>
																	<SelectItem value="none">
																		{t('ai.settings.noMusic')}
																	</SelectItem>
																	{MUSIC_GENRE_GROUPS.map((group) => (
																		<SelectItem
																			key={group.label}
																			value={group.label}
																		>
																			{group.label}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
														{selectedMusicGroup &&
															selectedMusicGroup !== 'none' && (
																<div className="space-y-2">
																	<label className="text-sm font-medium">
																		{t('ai.settings.song')}
																	</label>
																	<Select
																		value={selectedSong}
																		onValueChange={(val) => {
																			if (isPlayButtonClickRef.current) {
																				return
																			}
																			setSelectedSong(val)
																		}}
																	>
																		<SelectTrigger className="w-full">
																			<SelectValue
																				placeholder={t(
																					'ai.settings.selectSong'
																				)}
																			/>
																		</SelectTrigger>
																		<SelectContent>
																			{availableMusicGenres.map((genre) => (
																				<SelectItem
																					key={genre.value}
																					value={genre.value}
																				>
																					{genre.label}
																				</SelectItem>
																			))}
																		</SelectContent>
																	</Select>
																</div>
															)}
													</div>
												)}
											</div>
										)}
								</div>
							</div>

							<div
								className={`${generatedAudioUrl ? 'hidden md:block' : 'block'} w-full lg:w-[400px] md:border-l bg-card lg:px-6 py-3 lg:py-6 px-4 overflow-visible lg:overflow-auto`}
							>
								<h2 className="hidden md:block text-xl font-semibold mb-6">
									{t('ai.settings.title')}
								</h2>

								<div className="space-y-2 md:space-y-6 pb-24 md:pb-0">
									<div className="space-y-2 flex flex-col gap-1">
										<label className="text-sm font-medium">
											{t('ai.settings.accent')}
										</label>
										{isLoadingVoices ? (
											<Skeleton className="h-9 w-full bg-border dark:bg-accent" />
										) : isVoicesError ? (
											<div>
												<Select disabled>
													<SelectTrigger className="w-full">
														<SelectValue
															placeholder={t('ai.settings.voicesUnavailable')}
														/>
													</SelectTrigger>
													<SelectContent />
												</Select>
												<p className="text-xs text-destructive mt-1">
													{t('ai.settings.voicesErrorMessage')}
												</p>
											</div>
										) : (
											<Select
												value={selectedCollectionId}
												onValueChange={(val) => {
													setSelectedCollectionId(val)
													setSelectedVoice('')
												}}
												disabled={collectionOptions.length === 0}
											>
												<SelectTrigger className="w-full">
													<SelectValue
														placeholder={t('ai.settings.selectAccent')}
													/>
												</SelectTrigger>
												<SelectContent>
													{collectionOptions.map((coll) => (
														<SelectItem key={coll.value} value={coll.value}>
															{coll.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									</div>

									<div className="space-y-2 flex flex-col gap-1">
										<label className="text-sm font-medium">
											{t('ai.settings.voice')}
										</label>
										{isLoadingVoices ? (
											<Skeleton className="h-9 w-full bg-border dark:bg-accent" />
										) : isVoicesError ? (
											<div>
												<Select disabled>
													<SelectTrigger className="w-full">
														<SelectValue
															placeholder={t('ai.settings.voicesUnavailable')}
														/>
													</SelectTrigger>
													<SelectContent />
												</Select>
												<p className="text-xs text-destructive mt-1">
													{t('ai.settings.voicesErrorMessage')}
												</p>
											</div>
										) : (
											<Select
												value={selectedVoice}
												onValueChange={(val) => {
													// If this was triggered by a play button click, don't select
													// Don't reset flag here - let onOpenChange handle it
													if (isPlayButtonClickRef.current) {
														return
													}
													setSelectedVoice(val)
												}}
												open={voiceSelectOpen}
												onOpenChange={(open) => {
													// Prevent closing if it was a play button click
													if (!open && isPlayButtonClickRef.current) {
														isPlayButtonClickRef.current = false
														return
													}
													// Reset flag when closing normally (clicking outside)
													if (!open) {
														isPlayButtonClickRef.current = false
													}
													setVoiceSelectOpen(open)
												}}
												disabled={filteredVoices.length === 0}
											>
												<SelectTrigger
													className="w-full"
													data-tour="ai-ads-voice"
												>
													<SelectValue
														placeholder={t('ai.settings.selectVoice')}
													/>
												</SelectTrigger>
												<SelectContent>
													{filteredVoices.map((voice) => (
														<SelectItem
															key={voice.voiceId}
															value={voice.voiceId}
															className="flex items-center justify-between gap-2"
															onSelect={(e) => {
																// If clicking the play button, prevent selection and keep dropdown open
																if (isPlayButtonClickRef.current) {
																	e.preventDefault()
																	setVoiceSelectOpen(true) // Keep dropdown open
																	// Don't reset flag here - let onOpenChange handle it
																	return
																}
															}}
														>
															{voiceSelectOpen && (
																<button
																	type="button"
																	onMouseDown={(e) => {
																		e.preventDefault()
																		e.stopPropagation()
																		isPlayButtonClickRef.current = true
																	}}
																	onClick={async (e) => {
																		e.preventDefault()
																		e.stopPropagation()
																		isPlayButtonClickRef.current = true
																		await handleVoiceDemoPlayback(voice)
																	}}
																	className="h-5 w-5 rounded-full cursor-pointer bg-white text-black hover:bg-white/90 flex items-center justify-center flex-shrink-0 transition-all"
																>
																	{playingVoiceId === voice.voiceId &&
																	!isAudioPaused ? (
																		<Pause className="h-4 w-4 fill-current" />
																	) : (
																		<Play className="h-4 w-4 fill-current ml-0.5" />
																	)}
																</button>
															)}
															<span>{voice.getDisplayName()}</span>
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									</div>

									<button
										onClick={() => setShowSpeakerSettings(!showSpeakerSettings)}
										className="flex items-center gap-2 text-sm font-medium hover:text-muted-foreground transition-colors"
									>
										<ChevronDown
											className={`w-4 h-4 transition-transform ${
												showSpeakerSettings ? 'rotate-180' : ''
											}`}
										/>
										{showSpeakerSettings
											? t('ai.settings.hideSettings')
											: t('ai.settings.showSettings')}
									</button>

									{showSpeakerSettings && (
										<div className="space-y-6 pt-4">
											<div className="space-y-3">
												<div className="flex items-center justify-between text-sm">
													<span className="font-medium">
														{t('ai.settings.speed.slower')}
													</span>
													<span className="font-medium">
														{t('ai.settings.speed.faster')}
													</span>
												</div>
												<Slider
													value={speed}
													onValueChange={setSpeed}
													min={0}
													max={100}
													step={1}
													className="w-full"
												/>
											</div>

											<div className="space-y-3">
												<div className="flex items-center justify-between text-sm">
													<span className="font-medium">
														{t('ai.settings.expressiveness.expressive')}
													</span>
													<span className="font-medium">
														{t('ai.settings.expressiveness.neutral')}
													</span>
												</div>
												<Slider
													value={expressiveness}
													onValueChange={setExpressiveness}
													min={0}
													max={100}
													step={1}
													className="w-full"
												/>
											</div>

											<div className="space-y-3">
												<div className="flex items-center justify-between text-sm">
													<span className="font-medium">
														{t('ai.settings.clarity')}
													</span>
												</div>
												<Slider
													value={clarity}
													onValueChange={setClarity}
													min={0}
													max={100}
													step={1}
													className="w-full"
												/>
											</div>

											<div className="space-y-3">
												<div className="flex items-center justify-between text-sm">
													<span className="font-medium">
														{t('ai.settings.style')}
													</span>
												</div>
												<Slider
													value={styleExaggeration}
													onValueChange={setStyleExaggeration}
													min={0}
													max={100}
													step={1}
													className="w-full"
												/>
											</div>
										</div>
									)}

									<div className="space-y-4 pt-4">
										<div className="space-y-2 flex flex-col gap-1">
											<label className="text-sm font-medium">
												{t('ai.settings.musicStyle')}
											</label>
											<Select
												value={selectedMusicGroup}
												onValueChange={(val) => {
													// If this was triggered by a play button click, don't select
													// Don't reset flag here - let onOpenChange handle it
													if (isPlayButtonClickRef.current) {
														return
													}
													setSelectedMusicGroup(val)
												}}
												open={musicSelectOpen}
												onOpenChange={(open) => {
													// Prevent closing if it was a play button click
													if (!open && isPlayButtonClickRef.current) {
														isPlayButtonClickRef.current = false
														return
													}
													setMusicSelectOpen(open)
												}}
											>
												<SelectTrigger
													className="w-full"
													data-tour="ai-ads-music"
												>
													<SelectValue
														placeholder={t('ai.settings.selectMusicStyle')}
													/>
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">
														{t('ai.settings.noMusic')}
													</SelectItem>
													{MUSIC_GENRE_GROUPS.map((group) => (
														<SelectItem key={group.label} value={group.label}>
															{group.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										{selectedMusicGroup && selectedMusicGroup !== 'none' && (
											<div className="space-y-2">
												<label className="text-sm font-medium">
													{t('ai.settings.song')}
												</label>
												<Select
													value={selectedSong}
													onValueChange={(val) => {
														// If this was triggered by a play button click, don't select
														// Don't reset flag here - let onOpenChange handle it
														if (isPlayButtonClickRef.current) {
															return
														}
														setSelectedSong(val)
													}}
													open={songSelectOpen}
													onOpenChange={(open) => {
														// Prevent closing if it was a play button click
														if (!open && isPlayButtonClickRef.current) {
															isPlayButtonClickRef.current = false
															return
														}
														// Reset flag when closing normally (clicking outside)
														if (!open) {
															isPlayButtonClickRef.current = false
														}
														setSongSelectOpen(open)
													}}
												>
													<SelectTrigger className="w-full">
														<SelectValue
															placeholder={t('ai.settings.selectSong')}
														/>
													</SelectTrigger>
													<SelectContent>
														{availableMusicGenres.map((genre) => (
															<SelectItem
																key={genre.value}
																value={genre.value}
																className="flex items-center justify-between gap-2"
																onSelect={(e) => {
																	// If clicking the play button, prevent selection and keep dropdown open
																	if (isPlayButtonClickRef.current) {
																		e.preventDefault()
																		setSongSelectOpen(true) // Keep dropdown open
																		// Don't reset flag here - let onOpenChange handle it
																		return
																	}
																}}
															>
																{songSelectOpen && (
																	<button
																		type="button"
																		onMouseDown={(e) => {
																			e.preventDefault()
																			e.stopPropagation()
																			isPlayButtonClickRef.current = true
																		}}
																		onClick={async (e) => {
																			e.preventDefault()
																			e.stopPropagation()
																			isPlayButtonClickRef.current = true
																			await handleMusicDemoPlayback(genre.value)
																		}}
																		className="h-5 w-5 rounded-full cursor-pointer bg-white text-black hover:bg-white/90 flex items-center justify-center flex-shrink-0 transition-all"
																	>
																		{playingMusicId === genre.value &&
																		!isMusicPaused ? (
																			<Pause className="h-4 w-4 fill-current" />
																		) : (
																			<Play className="h-4 w-4 fill-current ml-0.5" />
																		)}
																	</button>
																)}
																<span>{genre.label}</span>
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{!generatedAudioUrl && (
				<div
					data-tour="ai-ads-generate-mobile"
					className="h-24 flex items-center justify-center md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 bg-card border-t border-border"
				>
					<Button
						onClick={handleGenerateSpeech}
						disabled={
							activeTab === 'text'
								? !scriptText.trim() || tokensAvailable <= 0 || isGenerating
								: activeTab === 'recording' && rawRecordingBlob
									? tokensAvailable <= 0 || isGenerating || !selectedVoice
									: tokensAvailable <= 0 || isGenerating
						}
						className="rounded-full w-full"
					>
						{isGenerating ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								{activeTab === 'recording' && rawRecordingBlob
									? t('ai.buttons.processing')
									: t('ai.buttons.generating')}
							</>
						) : activeTab === 'recording' && rawRecordingBlob ? (
							t('ai.buttons.process')
						) : (
							t('ai.buttons.generate')
						)}
					</Button>
				</div>
			)}
			{generatedAudioUrl && generatedAudioBlob && !isMixingAudio && (
				<div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 bg-card border-t border-border">
					<div className="space-y-2">
						<Button
							onClick={handleAssignToCampaign}
							className="rounded-full w-full"
						>
							{t('ai.buttons.assign_to_campaign')}
						</Button>
						<Button
							variant="outline"
							onClick={handleDownloadAudio}
							className="rounded-full w-full"
						>
							{t('ai.buttons.download')}
						</Button>
					</div>
				</div>
			)}
			{isAssignModalOpen && (
				<div className="md:hidden fixed inset-0 z-[60] bg-black/50">
					<div className="h-full w-full bg-card flex flex-col">
						<div className="flex items-center justify-between px-4 py-3 border-b border-border">
							<h2 className="text-2xl font-semibold">
								{t('ai.assign_modal.title')}
							</h2>
							<button
								type="button"
								onClick={handleCloseAssignModal}
								className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted/80 transition-colors"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<div className="px-4 py-4 flex-1 min-h-0">
							<div className="h-full border border-border rounded-2xl overflow-hidden bg-background flex flex-col">
								<div className="p-3 border-b border-border relative">
									<Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										value={assignSearchQuery}
										onChange={(e) => setAssignSearchQuery(e.target.value)}
										placeholder={t('ai.assign_modal.search_placeholder')}
										className="pl-9 h-10"
									/>
								</div>
								<ScrollArea className="flex-1">
									{isLoadingAssignCampaigns ? (
										<div className="h-full flex items-center justify-center text-sm text-muted-foreground">
											{t('ai.assign_modal.loading')}
										</div>
									) : filteredAssignCampaigns.length === 0 ? (
										<div className="h-full flex items-center justify-center text-sm text-muted-foreground">
											{t('ai.assign_modal.no_results')}
										</div>
									) : (
										<div className="divide-y divide-border">
											{filteredAssignCampaigns.map((campaign) => {
												const adsCount = campaign.adverts?.length || 0
												return (
													<button
														type="button"
														key={campaign.id}
														className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-accent/30 transition-colors"
														onClick={() =>
															setSelectedAssignCampaignId(
																selectedAssignCampaignId === campaign.id
																	? null
																	: campaign.id
															)
														}
													>
														<div className="min-w-0">
															<p className="text-base leading-tight font-medium truncate">
																{campaign.name}
															</p>
															<p className="text-xs text-muted-foreground mt-1">
																{getAdsCountLabel(adsCount)}
															</p>
														</div>
														<Checkbox
															checked={selectedAssignCampaignId === campaign.id}
															onCheckedChange={() =>
																setSelectedAssignCampaignId(
																	selectedAssignCampaignId === campaign.id
																		? null
																		: campaign.id
																)
															}
															onClick={(e) => e.stopPropagation()}
														/>
													</button>
												)
											})}
										</div>
									)}
								</ScrollArea>
							</div>
						</div>

						<div className="px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 border-t border-border bg-card">
							<Button
								onClick={handleConfirmAssignToCampaign}
								disabled={
									!selectedAssignCampaignId ||
									isAssigningCampaign ||
									uploadAdvertMutation.isPending ||
									updateCampaignMutation.isPending
								}
								className="w-full rounded-full"
							>
								{isAssigningCampaign ||
								uploadAdvertMutation.isPending ||
								updateCampaignMutation.isPending ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										{t('ai.assign_modal.assigning')}
									</>
								) : (
									t('ai.assign_modal.assign_button')
								)}
							</Button>
						</div>
					</div>
				</div>
			)}

			{generatedAudioUrl && generatedAudioBlob && !isMixingAudio && (
				<div className="hidden md:block">
					<AudioPlayer
						audioUrl={generatedAudioUrl}
						audioBlob={generatedAudioBlob}
						fileName={`${scriptText.substring(0, 30)}${scriptText.length > 30 ? '...' : ''}.mp3`}
						onDownload={handleDownloadAudio}
						onEdit={() => setIsEditModalOpen(true)}
						isAbsolute={true}
						closable={true}
						onClose={clearGeneratedAudio}
					/>
				</div>
			)}

			{generatedAudioUrl && generatedAudioBlob && isMixingAudio && (
				<div className="fixed bottom-0 lg:left-20 right-0 z-10 max-w-6xl lg:mx-auto w-full lg:mb-2">
					<div className="w-full dark:bg-background bg-muted rounded-xl overflow-hidden">
						<div className="flex items-center justify-between px-4 py-1 border-b border-border">
							<div className="h-4 w-48 bg-border/30 rounded animate-pulse" />
							<div className="h-8 w-8 bg-border/30 rounded-full animate-pulse" />
						</div>
						<div className="flex items-center gap-3 px-4 py-3">
							<div className="h-12 w-12 rounded-full bg-border/30 animate-pulse" />
							<div className="h-4 w-10 bg-border/30 rounded animate-pulse" />
							<div className="flex-1 h-12 rounded-md bg-border/30 animate-pulse" />
							<div className="h-4 w-10 bg-border/30 rounded animate-pulse" />
						</div>
					</div>
				</div>
			)}

			{originalVoiceBlob && (
				<EditAudioModal
					isOpen={isEditModalOpen}
					onClose={() => setIsEditModalOpen(false)}
					voiceAudioUrl={elevenLabsService.createAudioUrl(originalVoiceBlob)}
					voiceAudioBlob={originalVoiceBlob}
					initialMusicGroup={selectedMusicGroup}
					initialMusicGenre={selectedSong}
					initialMusicStart={musicRegionForEditor?.start}
					initialMusicEnd={musicRegionForEditor?.end}
					onSave={handleSaveEditedAudio}
				/>
			)}

			<NoTokensModal
				isOpen={showNoTokensModal}
				onClose={() => setShowNoTokensModal(false)}
				availableTokens={tokensAvailable}
				maxTokens={tokensTotal}
			/>
		</AuthenticatedLayout>
	)
}

export default CreateAICampaignPage
